/*
 * live-inventory.js
 * -----------------------------------------------------------------------------
 * Purpose:
 *   Some shoppers (especially those arriving through Instagram / Facebook in-app
 *   browsers) can be served STALE product-page HTML from cache. In that HTML a
 *   size/variant may be baked in as "Sold out" even though it has since been
 *   restocked. The live data at /products/{handle}.js is always correct.
 *
 *   This script re-validates the rendered variant UI against that live data after
 *   the page loads, and corrects FALSE "sold out" states in place (no reload, no
 *   flash).
 *
 *   This store has TWO product templates, so we support both markup styles:
 *
 *   A) Standard Dawn (sections/main-product.liquid) inside <product-info>:
 *        - button picker : <input type="radio" class="disabled"> (+ label)
 *        - swatch picker : <input class="swatch-input__input visually-disabled">
 *        - dropdown      : <option> whose text was replaced with "… - unavailable"
 *        - buy button    : #ProductSubmitButton-{sectionId} (disabled attribute)
 *        - selected blob : <script type="application/json" data-selected-variant>
 *
 *   B) Custom "Product - Three Column" (sections/product-three-column.liquid):
 *        - option buttons: <button class="variant-option variant-option--disabled"
 *                            data-option-position data-option-value disabled>
 *        - buy button    : shared buy-buttons snippet (.product-form__submit)
 *        - The section bakes a `const variants = {{ product.variants | json }}`
 *          closure that its own click logic reads. We can't reach that closure
 *          directly, so after correcting the DOM we dispatch a
 *          `live-inventory:variants` CustomEvent carrying the corrected
 *          availability; a tiny listener in that section patches its closure so
 *          later clicks stay correct.
 *
 * Safety / constraints:
 *   - Progressive enhancement: any failure leaves the page exactly as-is.
 *   - One-directional: we ONLY clear false "sold out" states (turn unavailable ->
 *     available). We NEVER introduce a new "sold out" and NEVER disable/hide a
 *     variant that live data reports as available.
 *   - In-place DOM updates only (no reload, no flash).
 *   - Does not touch cart or checkout code.
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Bail early if the browser lacks the APIs we rely on. Progressive enhancement:
  // if anything is missing we simply leave the cached HTML alone.
  if (typeof window.fetch !== 'function' || typeof document.querySelector !== 'function') return;

  /**
   * Derive the product handle from the theme's existing data, with fallbacks.
   * Returns null if we can't determine it.
   */
  function getProductHandle(productInfo) {
    // <product-info data-url="/products/the-handle"> is the theme's own source.
    var url = productInfo && productInfo.dataset ? productInfo.dataset.url : null;
    if (url) {
      var afterProducts = url.split('/products/')[1];
      if (afterProducts) return afterProducts.split(/[?#]/)[0];
    }
    // Fallback: ShopifyAnalytics meta (works regardless of template).
    try {
      var handle = window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product &&
        window.ShopifyAnalytics.meta.product.handle;
      if (handle) return handle;
    } catch (e) {
      /* ignore */
    }
    // Fallback: parse the current path (/products/the-handle).
    var path = window.location && window.location.pathname;
    if (path && path.indexOf('/products/') !== -1) {
      var seg = path.split('/products/')[1];
      if (seg) return seg.split(/[?#]/)[0];
    }
    return null;
  }

  /** Normalize an option value string for comparison. */
  function norm(value) {
    return (value == null ? '' : String(value)).trim();
  }

  /**
   * Determine whether an option value at `position` should be selectable, given
   * the live variants and the values currently selected for the OTHER options.
   * Mirrors Dawn's server-side `option_value.available` logic.
   *
   * @param {Array} variants  live variants
   * @param {Array} selected  [{ position, selectedValue }] for every option group
   */
  function valueIsAvailable(variants, selected, position, value) {
    return variants.some(function (variant) {
      if (!variant.available) return false;
      if (norm(variant.options[position - 1]) !== value) return false;
      return selected.every(function (g) {
        if (g.position === position) return true;
        if (!g.selectedValue) return true; // nothing selected yet for that option
        return norm(variant.options[g.position - 1]) === g.selectedValue;
      });
    });
  }

  /* ---------------------------------------------------------------------------
   * A) Standard Dawn (<variant-selects> inside <product-info>)
   * ------------------------------------------------------------------------ */

  function readDawnOptionGroups(variantSelects) {
    var groups = [];
    var containers = variantSelects.querySelectorAll(
      'fieldset.product-form__input, .product-form__input--dropdown'
    );

    containers.forEach(function (container, index) {
      var position = index + 1; // option1 / option2 / option3 (document order)
      var select = container.querySelector('select');

      if (select) {
        var options = Array.prototype.slice.call(select.querySelectorAll('option'));
        groups.push({
          position: position,
          selectedValue: norm(select.value),
          inputs: options.map(function (opt) {
            return { el: opt, value: norm(opt.value), type: 'option' };
          }),
        });
      } else {
        var radios = Array.prototype.slice.call(container.querySelectorAll('input[type="radio"]'));
        if (!radios.length) return;
        var checked = container.querySelector('input[type="radio"]:checked');
        groups.push({
          position: position,
          selectedValue: checked ? norm(checked.value) : '',
          inputs: radios.map(function (input) {
            return {
              el: input,
              value: norm(input.value),
              type: input.classList.contains('swatch-input__input') ? 'swatch' : 'radio',
            };
          }),
        });
      }
    });

    return groups;
  }

  /** Clear a false "sold out" state on a single Dawn option input (enable only). */
  function clearDawnSoldOut(input) {
    if (input.type === 'radio') {
      // Button picker: unavailable is represented by class="disabled".
      input.el.classList.remove('disabled');
    } else if (input.type === 'swatch') {
      // Swatch picker: unavailable is the "visually-disabled" class.
      input.el.classList.remove('visually-disabled');
    } else if (input.type === 'option') {
      // Dropdown: text was replaced with "value - unavailable"; restore label.
      if (norm(input.el.textContent) !== norm(input.el.value)) {
        input.el.textContent = input.el.value;
      }
      if (input.el.disabled) input.el.disabled = false;
    }
  }

  /** Patch Dawn's selected-variant JSON blob (upward only) for variant-change logic. */
  function patchSelectedVariantBlob(variantSelects, variantsById) {
    var blob = variantSelects.querySelector('script[type="application/json"][data-selected-variant]');
    if (!blob) return;
    try {
      var selected = JSON.parse(blob.textContent);
      if (!selected || selected.id == null) return;
      var live = variantsById[selected.id];
      if (live && live.available && !selected.available) {
        selected.available = true;
        blob.textContent = JSON.stringify(selected);
      }
    } catch (e) {
      /* leave the blob untouched on parse failure */
    }
  }

  function reconcileDawn(productInfo, variants, variantsById) {
    var variantSelects = productInfo.querySelector('variant-selects');
    var selectedLiveVariant = null;

    if (variantSelects) {
      var groups = readDawnOptionGroups(variantSelects);

      // 1) Clear false "sold out" states on every option value.
      groups.forEach(function (group) {
        group.inputs.forEach(function (input) {
          if (valueIsAvailable(variants, groups, group.position, input.value)) {
            clearDawnSoldOut(input);
          }
        });
      });

      // 2) Keep the theme's variant-change logic in sync.
      patchSelectedVariantBlob(variantSelects, variantsById);

      // Resolve the currently selected variant from the (now patched) blob.
      var blob = variantSelects.querySelector('script[type="application/json"][data-selected-variant]');
      if (blob) {
        try {
          var selected = JSON.parse(blob.textContent);
          if (selected && selected.id != null) selectedLiveVariant = variantsById[selected.id];
        } catch (e) {
          /* ignore */
        }
      }
    }

    // Single-variant products still have a buy button.
    if (!selectedLiveVariant && variants.length === 1) selectedLiveVariant = variants[0];

    reconcileBuyButton(productInfo, selectedLiveVariant);
  }

  /* ---------------------------------------------------------------------------
   * B) Custom "Product - Three Column"
   * ------------------------------------------------------------------------ */

  function reconcileThreeColumn(root, variants, variantsById) {
    var buttons = Array.prototype.slice.call(root.querySelectorAll('.variant-option[data-option-position]'));
    if (!buttons.length) {
      // No multi-variant picker (default variant only) – still reconcile the button.
      reconcileBuyButton(root, variants.length === 1 ? variants[0] : null);
      return;
    }

    // Build the list of option groups and which value (if any) is selected in each.
    var selected = [];
    var seenPositions = {};
    buttons.forEach(function (btn) {
      var position = parseInt(btn.dataset.optionPosition, 10);
      if (!position || seenPositions[position]) return;
      seenPositions[position] = true;
      var grid = btn.closest('.variant-options-grid');
      var selectedBtn = grid ? grid.querySelector('.variant-option.selected') : null;
      selected.push({
        position: position,
        selectedValue: selectedBtn ? norm(selectedBtn.dataset.optionValue) : '',
      });
    });

    // Clear false "sold out" states on each option button (enable only).
    buttons.forEach(function (btn) {
      var position = parseInt(btn.dataset.optionPosition, 10);
      var value = norm(btn.dataset.optionValue);
      if (valueIsAvailable(variants, selected, position, value)) {
        btn.classList.remove('variant-option--disabled');
        if (btn.disabled) btn.disabled = false;
      }
    });

    // Reconcile the buy button if a full variant is selected and live-available.
    var selectedLiveVariant = resolveSelectedVariant(variants, selected);
    if (selectedLiveVariant) reconcileBuyButton(root, selectedLiveVariant);
  }

  /** Resolve the variant matching all currently-selected option values, or null. */
  function resolveSelectedVariant(variants, selected) {
    var allChosen = selected.every(function (g) {
      return g.selectedValue;
    });
    if (!allChosen || !selected.length) return null;
    return (
      variants.filter(function (variant) {
        return selected.every(function (g) {
          return norm(variant.options[g.position - 1]) === g.selectedValue;
        });
      })[0] || null
    );
  }

  /* ---------------------------------------------------------------------------
   * Shared buy button reconciliation (enable only; never disables).
   * ------------------------------------------------------------------------ */

  function reconcileBuyButton(root, selectedLiveVariant) {
    if (!selectedLiveVariant || !selectedLiveVariant.available) return;

    var button = root.querySelector('.product-form__submit');
    if (!button) return;

    // Prefer the theme's own API so internal state stays consistent.
    var productForm = root.querySelector('product-form');
    if (productForm && typeof productForm.toggleSubmitButton === 'function') {
      if (button.hasAttribute('disabled')) {
        productForm.toggleSubmitButton(false); // resets label to "Add to cart"
      }
    } else if (button.hasAttribute('disabled') || button.disabled) {
      button.removeAttribute('disabled');
      button.disabled = false;
      var label = button.querySelector('span');
      if (label && window.variantStrings && window.variantStrings.addToCart) {
        label.textContent = window.variantStrings.addToCart;
      }
    }

    // Belt-and-suspenders: ensure the variant id input can submit.
    var idInput = root.querySelector('.product-variant-id, input[name="id"]');
    if (idInput && idInput.disabled) idInput.disabled = false;
  }

  /* ---------------------------------------------------------------------------
   * Entry point
   * ------------------------------------------------------------------------ */

  function init() {
    var productInfo = document.querySelector('product-info[id^="MainProduct"]') ||
      document.querySelector('product-info');
    var threeColumn = document.querySelector('.product-three-column');

    if (!productInfo && !threeColumn) return; // not a product page we handle

    var handle = getProductHandle(productInfo);
    if (!handle) return;

    // cache: 'no-store' is critical inside aggressive in-app webviews so we read
    // truly live inventory rather than another cached copy.
    fetch('/products/' + encodeURIComponent(handle) + '.js', { cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('Bad response ' + response.status);
        return response.json();
      })
      .then(function (product) {
        if (!product || !Array.isArray(product.variants)) return;

        var variants = product.variants;
        var variantsById = {};
        var availableById = {};
        var inventoryById = {};
        variants.forEach(function (v) {
          variantsById[v.id] = v;
          availableById[v.id] = !!v.available;
          if (typeof v.inventory_quantity === 'number') inventoryById[v.id] = v.inventory_quantity;
        });

        try {
          if (productInfo) reconcileDawn(productInfo, variants, variantsById);
        } catch (e) {
          if (window.console) console.warn('[live-inventory] Dawn reconcile skipped:', e);
        }

        try {
          if (threeColumn) reconcileThreeColumn(threeColumn, variants, variantsById);
        } catch (e) {
          if (window.console) console.warn('[live-inventory] three-column reconcile skipped:', e);
        }

        // Feed corrected availability into any section closures that baked in
        // stale variant data (e.g. the three-column section). One-directional
        // patching is the listener's responsibility.
        try {
          document.dispatchEvent(
            new CustomEvent('live-inventory:variants', {
              detail: { availableById: availableById, inventoryById: inventoryById },
            })
          );
        } catch (e) {
          /* CustomEvent unsupported – ignore */
        }
      })
      .catch(function (e) {
        // Network/parse failure: leave the page exactly as rendered.
        if (window.console) console.warn('[live-inventory] live check skipped:', e);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
